/**
 * 発電販売計画詳細 - 合計・需給バランス算出と前回差分判定の純関数。
 *
 * 未入力集約規則（鉄則、ProcurementPlanDetail/models/calc.ts と同方針）:
 *   - 構成内訳セルが 1 つでも空（undefined）なら合計も undefined（空）
 *   - 全て入力済み（0 含む）のときのみ合算
 *   - 需給バランスは全 3 合計が数値のときのみ算出
 *
 * 値は number（kWh 整数）または undefined（未入力）で表現する。
 * bigint（proto PlanSlot.valueKwh）→ number 変換はこの層で行う。
 *
 * 需給バランスの計算式（spec AC-006、generation-sales-plan-detail.md）:
 *   需給バランス = 発電計画合計 + 調達計画合計 − 販売計画合計
 * 需要調達計画（ProcurementPlanDetail）の「調達合計 − 販売合計」とは異なる。
 *
 * 本画面は read-only（ProcurementPlanDetail と同じく、計画値の生成は
 * backend worker が担う。frontend は表示専用）。
 */
import type {
  GenerationSalesPlanBreakdownRow,
  GenerationSalesPlanGenerationRow,
} from '@ebalance/proto/bffuser/v1/generation_sales_plan_model_pb'
import type { PlanSlot } from '@ebalance/proto/bffuser/v1/procurement_plan_model_pb'

/** 1 コマ（30分）の値。undefined = 未入力、number = 入力済（0 含む） */
export type SlotValue = number | undefined

/**
 * bigint | undefined（proto PlanSlot.valueKwh）を SlotValue（number | undefined）に変換する。
 * proto の optional int64 は bigint で表現される。
 */
export function toSlotValue(slot: PlanSlot | undefined): SlotValue {
  const kwh = slot?.valueKwh
  if (kwh === undefined) return undefined
  return Number(kwh)
}

/**
 * PlanSlot 配列（48 要素）を SlotValue[] に変換する。
 *
 * 本画面は read-only の「生成済み」計画値を表示する。生成された内訳行は
 * 全コマが確定済みで、proto に値が無いコマ（worker が option.None で出す
 * 取引なしコマ）は業務的に「取引なし = 0」を意味する。したがって表示用
 * SlotValue では未設定コマを 0 に倒す（"-"（cell.empty）にしない。
 * ProcurementPlanDetail/models/calc.ts rowToSlots と同方針）。
 */
export function rowToSlots(slots: PlanSlot[]): SlotValue[] {
  return slots.map((s) => toSlotValue(s) ?? 0)
}

/**
 * 複数行の 48 コマ分を「行をまたいだコマ単位合計」に変換する。
 * 1 つでも未入力（undefined）があれば当該コマは undefined を返す（鉄則：空を 0 に潰さない）。
 * 行が 0 件（空配列）の場合は 48 コマ全て undefined を返す。
 */
export function calcSlotTotal(slots: SlotValue[][]): SlotValue[] {
  if (slots.length === 0) return Array<SlotValue>(48).fill(undefined)
  return Array.from({ length: 48 }, (_, i) => {
    let total = 0
    for (const row of slots) {
      const v = row[i]
      if (v === undefined) return undefined
      total += v
    }
    return total
  })
}

/**
 * 発電BG別内訳行（GenerationSalesPlanGenerationRow[]）から発電計画合計（48 コマ）を算出する。
 * 発電BG単位の内訳は列としては表示しない（発電所別内訳は別画面 `/gen-plan-detail` の責務、
 * spec スコープ外）。本画面では合計 1 列のみを表示する。
 */
export function calcGenerationTotal(
  rows: GenerationSalesPlanGenerationRow[],
): SlotValue[] {
  return calcSlotTotal(rows.map((r) => rowToSlots(r.slots)))
}

/**
 * 取引先別内訳行（GenerationSalesPlanBreakdownRow[]。販売 / 調達共通）から合計（48 コマ）を算出する。
 */
export function calcBreakdownTotal(
  rows: GenerationSalesPlanBreakdownRow[],
): SlotValue[] {
  return calcSlotTotal(rows.map((r) => rowToSlots(r.slots)))
}

/**
 * 需給バランスを算出する（spec AC-006: 発電計画合計 + 調達計画合計 − 販売計画合計）。
 * いずれかが未入力なら undefined を返す。
 */
export function calcSupplyDemandBalance(
  generationTotal: SlotValue,
  procurementTotal: SlotValue,
  salesTotal: SlotValue,
): SlotValue {
  if (
    generationTotal === undefined ||
    procurementTotal === undefined ||
    salesTotal === undefined
  ) {
    return undefined
  }
  return generationTotal + procurementTotal - salesTotal
}

/**
 * 前回差分判定: current と previous のコマ値が異なるかを判定する。
 *
 * 比較ルール:
 *   - current === previous なら false（差分なし）
 *   - どちらかが undefined で他方が数値なら true（差分あり）
 *   - 両方 undefined なら false（差分なし）
 */
export function isDiffCell(current: SlotValue, previous: SlotValue): boolean {
  return current !== previous
}

/**
 * 列キー（取引先別内訳の一致判定に使用）。
 * sourceType + counterpartyCode + contractId で列を特定する。
 */
export function makeColumnKey(
  sourceType: number,
  counterpartyCode: string | undefined,
  contractId: string | undefined,
): string {
  return `${sourceType}|${counterpartyCode ?? ''}|${contractId ?? ''}`
}

/**
 * 発電計画の前回差分判定（spec AC-031: 前回差分は発電所単位の系統コード
 * = generationBgCode ベースで判定する。発電量を変更したコマのみ差分が出る）。
 *
 * index.tsx の旧実装は全 BG 合算後の発電計画合計同士を比較していたため、
 * BG-A +100kWh / BG-B -100kWh のように合算値が相殺されるケースで差分が
 * 検出できない false negative があった（AC-031 違反）。本関数は BG 単位で
 * マッチングしてから比較することでこれを解消する。
 *
 * 片側にしか存在しない BG は、存在しない側を全コマ 0 とみなして比較する
 * （rowToSlots の「取引なし = 0」方針と整合）。breakdown 列の calcColumnDiff も
 * 前回に対応列がない列を前回値 0 として比べる（AC-PLC-013）ため、同じ規則。
 *
 * コマ i の差分 = いずれかの bgCode で current[i] !== previous[i]。
 */
export function calcGenerationDiffFlags(
  currentRows: GenerationSalesPlanGenerationRow[],
  previousRows: GenerationSalesPlanGenerationRow[],
): boolean[] {
  const buildBgSlotsMap = (
    rows: GenerationSalesPlanGenerationRow[],
  ): Map<string, SlotValue[]> => {
    const m = new Map<string, SlotValue[]>()
    for (const row of rows) {
      const slots = rowToSlots(row.slots)
      const existing = m.get(row.generationBgCode)
      // 同一 bgCode の複数行は防御的に合算する（未入力は 0 扱い、rowToSlots が
      // 既に取引なしコマを 0 に倒しているため slots は常に number）。
      m.set(
        row.generationBgCode,
        existing === undefined
          ? slots
          : existing.map((v, i) => (v ?? 0) + (slots[i] ?? 0)),
      )
    }
    return m
  }

  const currentMap = buildBgSlotsMap(currentRows)
  const previousMap = buildBgSlotsMap(previousRows)
  const bgCodes = new Set<string>([...currentMap.keys(), ...previousMap.keys()])
  const zeroSlots = (): SlotValue[] => Array<SlotValue>(48).fill(0)

  const flags = Array<boolean>(48).fill(false)
  for (const bgCode of bgCodes) {
    const current = currentMap.get(bgCode) ?? zeroSlots()
    const previous = previousMap.get(bgCode) ?? zeroSlots()
    for (let i = 0; i < 48; i++) {
      if (isDiffCell(current[i], previous[i])) {
        flags[i] = true
      }
    }
  }
  return flags
}

/**
 * 前回提出からの変更コマ数を算出する（spec AC-032: 提出確認ダイアログの
 * サマリー「前回提出からの変更: {{count}} コマ」）。
 *
 * 発電計画合計の差分（generationDiffFlags）だけでなく、販売・調達の内訳列
 * （diffFlags を持つ列オブジェクト配列）も横断し、コマ index 0..47 のうち
 * いずれか 1 つでも差分があるコマ数を union 集計する。発電計画合計は不変でも
 * 販売・調達の内訳だけが変わったコマを見逃さないため（発電合計のみで判定すると
 * 「変更: 0 コマ」と誤表示され、ユーザーが変更なしと誤認して提出してしまう）。
 */
export function calcChangedSlotCount(
  generationDiffFlags: boolean[],
  salesColumns: Array<{ diffFlags: boolean[] }>,
  procurementColumns: Array<{ diffFlags: boolean[] }>,
): number {
  let count = 0
  for (let i = 0; i < 48; i++) {
    const changed =
      generationDiffFlags[i] === true ||
      salesColumns.some((c) => c.diffFlags[i] === true) ||
      procurementColumns.some((c) => c.diffFlags[i] === true)
    if (changed) count++
  }
  return count
}

/**
 * 前回提出の列と現在の列をキーでマッチングし、
 * 各コマで差分があるかの boolean[48] を返す。
 * 前回に対応列がない場合は前回値 0 として比べる（AC-PLC-013）。
 */
export function calcColumnDiff(
  currentSlots: SlotValue[],
  previousColumnsMap: Map<string, SlotValue[]>,
  key: string,
): boolean[] {
  // 前回に対応列がない列（後から足した取引先・契約）は前回値 0 として比べる
  // （AC-PLC-013: 列が無いことと 0 は計画上同じ意味。値が 0 から変わったコマだけ差分）。
  const prevSlots = previousColumnsMap.get(key)
  return currentSlots.map((v, i) => isDiffCell(v, prevSlots?.[i] ?? 0))
}
